import { http } from "../http";

/**
 * Admin-API:t för sensorerna: aktivitetsmätning och fjärruppdatering.
 *
 * Gränserna nedan speglar activitySanitizeConfig() i firmwarens
 * activity.cpp. Servern validerar mot samma värden — det här är bara för
 * att inputfälten ska kunna säga ifrån innan requesten går iväg.
 */

export type CrowdLevel = "UNKNOWN" | "QUIET" | "MODERATE" | "BUSY" | "FULL";

export type ActivitySample = {
  id: string;
  measuredAt: string;
  ageMinutes: number;
  schemaVersion: number;
  activityIndex: number | null;
  crowdLevel: CrowdLevel;
  confidence: number;
  sampleWindowSeconds: number;
  sources: string[];
  baselineValid: boolean;

  wifi: {
    managementPacketsPerSecond: number | null;
    dataPacketsPerSecond: number | null;
    meanRssi: number | null;
    sampleSeconds: number | null;
  } | null;

  ble: {
    advertisementsPerSecond: number | null;
    meanRssi: number | null;
    sampleSeconds: number | null;
  } | null;

  audio: {
    rmsDbfs: number | null;
    peakDbfs: number | null;
    clippingRatio: number | null;
    observedSamples: number | null;
  } | null;

  /** Om mätningen når ut till kartan. */
  publicVisible: boolean;
  /** stale | no_baseline | no_index | low_confidence, eller null. */
  hiddenReason: string | null;
};

/**
 * Mätinställningarna kommer från venuet och ställs under Edit venue. En
 * okopplad sensor har bara enabled: false.
 */
export type SensorActivityConfig = {
  enabled: boolean;
  intervalSeconds?: number;
  wifiEnabled?: boolean;
  bleEnabled?: boolean;
  audioEnabled?: boolean;
  wifiSampleSeconds?: number;
  bleSampleSeconds?: number;
  wifiMinRssi?: number;
  bleMinRssi?: number;
  baselineRequested: boolean;
  baselineCalibratedAt: string | null;
  latestSample: ActivitySample | null;
};

export type SensorOta = {
  enabled: boolean;
  firmwareVersion: string | null;
  target: { id: string; version: string; status: string } | null;
  /** pending | success | failed, eller null när inget provats. */
  status: string | null;
  message: string | null;
  updatedAt: string | null;
  attempts: number;
  attemptVersion: string | null;
};

/**
 * HARDWARE = ESP32-lådan med mikrofon. SOFTWARE = Beatspot Agent på
 * venuets dator, som läser operativsystemets Now Playing. Agenten har
 * ingen mikrofon, aktivitetsmätning eller firmware — de inställningarna
 * gäller inte den, och servern nekar dem.
 */
export type SensorDeviceType = "HARDWARE" | "SOFTWARE";

export type AdminSensor = {
  id: string;
  sensorId: string;
  installationName: string | null;
  name: string | null;
  venue: { id: string; name: string; plan: string } | null;
  claimStatus: "UNCLAIMED" | "CLAIMED";
  deviceType: SensorDeviceType;
  /** Bara SOFTWARE: WINDOWS | MACOS. */
  platform: string | null;
  /** Bara SOFTWARE: Beatspot Agent-versionen. */
  agentVersion: string | null;
  capabilities: string[];
  online: boolean;
  isActive: boolean;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  lastStatus: string | null;

  /** Från venuet. Null när sensorn inte är kopplad. */
  recordSeconds: number | null;
  intervalSeconds: number | null;
  micShift: number;
  makeupGain: number;
  compressor: boolean;
  tuneRequested: boolean;

  activity: SensorActivityConfig;
  ota: SensorOta;
};

export type ActivityLimits = {
  intervalSeconds: { min: number; max: number };
  wifiSampleSeconds: { min: number; max: number };
  bleSampleSeconds: { min: number; max: number };
  minRssi: { min: number; max: number };
};

export type SensorListResponse = {
  limits: ActivityLimits;
  items: AdminSensor[];
};

const PLATFORM_LABELS: Record<string, string> = {
  WINDOWS: "Windows",
  MACOS: "macOS",
};

/** "Software · Windows" eller "Hardware". */
export function sensorKindLabel(sensor: AdminSensor) {
  if (sensor.deviceType !== "SOFTWARE") return "Hardware";
  const platform = sensor.platform ? PLATFORM_LABELS[sensor.platform] ?? sensor.platform : null;
  return platform ? `Software · ${platform}` : "Software";
}

/** "Beatspot Agent 1.0.0" eller "FW 1.3.5". */
export function sensorVersionLabel(sensor: AdminSensor) {
  if (sensor.deviceType === "SOFTWARE") {
    return `Beatspot Agent ${sensor.agentVersion ?? "unknown"}`;
  }
  return `FW ${sensor.ota.firmwareVersion ?? "unknown"}`;
}

export function listSensors() {
  return http.get<SensorListResponse>("/admin/sensors");
}

export type SensorSetupCode = {
  setupCode: string;
  expiresAt: string;
};

/** Plaintext setupCode exists only in this create response. */
export function createSensorSetupCode(expiresInMinutes = 30) {
  return http.post<{ ok: true } & SensorSetupCode>("/admin/sensor-setup-codes", {
    expiresInMinutes,
  });
}

export function listSensorActivity(sensorId: string, limit = 100) {
  return http.get<{
    sensorId: string;
    freshMinutes: number;
    minPublicConfidence: number;
    items: ActivitySample[];
  }>(`/admin/sensors/${encodeURIComponent(sensorId)}/activity?limit=${limit}`);
}

/**
 * Det som hör till själva lådan. Intervall och aktivitetsmätning ställs
 * på venuet (patchVenue, sensorSettings), inte här.
 */
export type SensorPatch = Partial<{
  installationName: string;
  isActive: boolean;
  venueId: string | null;
  tuneRequested: boolean;
  activityBaselineRequested: boolean;

  otaEnabled: boolean;
  otaTargetId: string | null;
}>;

export function updateSensor(sensorId: string, patch: SensorPatch) {
  return http.patch<{ ok: true; sensor: AdminSensor }>(
    `/admin/sensors/${encodeURIComponent(sensorId)}`,
    patch
  );
}

export type DeleteSensorResult = {
  ok: true;
  sensorId: string;
  detachedPlays: number;
  deletedSamples: number;
  credentialRevoked: boolean;
};

/**
 * Tar bort sensorposten. Uppspelningarna blir kvar hos venuen, bara
 * lossade från sensorn.
 *
 * revokeCredential spärrar dessutom lådans token. Utan den registrerar
 * sig lådan som oansluten igen nästa gång den hör av sig — vilket är
 * det man vill vid en flytt, men inte när hårdvaran är borta.
 */
export function deleteSensor(
  sensorId: string,
  options: { revokeCredential?: boolean } = {}
) {
  return http.delete<DeleteSensorResult>(
    `/admin/sensors/${encodeURIComponent(sensorId)}`,
    { revokeCredential: options.revokeCredential === true }
  );
}
